import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Trash2, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Contextual help triggers configuration
const CONTEXTUAL_TRIGGERS = {
  '/checkout': {
    delay: 30000, // 30 seconds
    message: "Need help with checkout? I can explain shipping options, fees, or payment methods!",
    icon: "💳"
  },
  '/sell': {
    delay: 25000,
    message: "Creating your first listing? I can help with grading descriptions, photo tips, or pricing strategy!",
    icon: "📝"
  },
  '/cards/': {
    delay: 20000,
    message: "Interested in this card? I can tell you about our buyer protection or shipping policies!",
    icon: "🃏"
  },
  '/subscription': {
    delay: 20000,
    message: "Considering membership? I can explain the difference between Gold and Elite tiers!",
    icon: "⭐"
  },
  '/scanner': {
    delay: 15000,
    message: "Using our card scanner? I can explain how the AI grading works!",
    icon: "📷"
  },
  '/marketplace': {
    delay: 45000,
    message: "Looking for something specific? I can help you find cards or explain our Fair Exposure system!",
    icon: "🔍"
  },
  '/seller-hub': {
    delay: 25000,
    message: "Managing your listings? I can help with bulk actions, tracking, or printing shipping labels!",
    icon: "📦"
  }
};

const AIChatbot = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [proactiveMessage, setProactiveMessage] = useState(null);
  const [showProactiveBubble, setShowProactiveBubble] = useState(false);
  
  const messagesEndRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const contextualTimeoutRef = useRef(null);
  const lastTriggeredPath = useRef(null);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Contextual help trigger based on page and time spent
  useEffect(() => {
    // Clear existing timeout when page changes
    if (contextualTimeoutRef.current) {
      clearTimeout(contextualTimeoutRef.current);
    }
    
    // Don't show if chatbot is already open
    if (isOpen) return;
    
    // Find matching trigger
    const currentPath = location.pathname;
    let matchedTrigger = null;
    let matchedPath = null;
    
    for (const [path, config] of Object.entries(CONTEXTUAL_TRIGGERS)) {
      if (currentPath.startsWith(path) || currentPath.includes(path)) {
        matchedTrigger = config;
        matchedPath = path;
        break;
      }
    }
    
    // Don't trigger again for same path in same session
    if (matchedTrigger && matchedPath !== lastTriggeredPath.current) {
      contextualTimeoutRef.current = setTimeout(() => {
        // Check again if chatbot is still closed
        if (!isOpen) {
          setProactiveMessage(matchedTrigger);
          setShowProactiveBubble(true);
          lastTriggeredPath.current = matchedPath;
          
          // Auto-hide after 10 seconds if not interacted
          setTimeout(() => {
            setShowProactiveBubble(false);
          }, 10000);
        }
      }, matchedTrigger.delay);
    }
    
    return () => {
      if (contextualTimeoutRef.current) {
        clearTimeout(contextualTimeoutRef.current);
      }
    };
  }, [location.pathname, isOpen]);

  // Scroll opacity effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Set timeout to restore opacity after scroll stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Load session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem('chatbot_session');
    if (savedSession) {
      setSessionId(savedSession);
      loadChatHistory(savedSession);
    }
  }, []);

  // Fetch suggestions
  useEffect(() => {
    fetch(`${API_URL}/api/chatbot/suggestions`)
      .then(res => res.json())
      .then(data => setSuggestions(data.suggestions || []))
      .catch(err => console.error('Failed to load suggestions:', err));
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/api/chatbot/history/${sid}`);
      const data = await response.json();
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages.map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.created_at
        })));
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chatbot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      // Save session ID
      if (!sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('chatbot_session', data.session_id);
      }

      const botMessage = { 
        role: 'assistant', 
        content: data.response, 
        timestamp: new Date().toISOString() 
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  const clearHistory = async () => {
    if (sessionId) {
      try {
        await fetch(`${API_URL}/api/chatbot/history/${sessionId}`, { method: 'DELETE' });
      } catch (error) {
        console.error('Failed to clear history:', error);
      }
    }
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem('chatbot_session');
  };

  // Calculate position based on mobile/desktop
  const getLauncherPosition = useCallback(() => {
    if (isMobile) {
      // On mobile, position higher to avoid Buy Now buttons
      return { bottom: '80px', right: '16px' };
    }
    // On desktop, position above the Emergent badge
    return { bottom: '70px', right: '24px' };
  }, [isMobile]);

  const launcherPosition = getLauncherPosition();

  // Handle opening chat from proactive message
  const handleProactiveClick = () => {
    setShowProactiveBubble(false);
    setIsOpen(true);
    // Pre-fill with the contextual question
    if (proactiveMessage) {
      setTimeout(() => {
        setInputValue(proactiveMessage.message.split('?')[0] + '?');
      }, 500);
    }
  };

  return (
    <>
      {/* Proactive Help Bubble */}
      {!isOpen && showProactiveBubble && proactiveMessage && (
        <div 
          className="fixed z-[9997] animate-in slide-in-from-right-5 fade-in duration-300"
          style={{
            ...launcherPosition,
            bottom: isMobile ? '130px' : '120px',
            right: isMobile ? '16px' : '24px',
          }}
        >
          <div 
            onClick={handleProactiveClick}
            className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-4 shadow-2xl cursor-pointer hover:border-amber-500/50 transition-all max-w-[280px]"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{proactiveMessage.icon}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-medium text-amber-400">SlabBot</span>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  {proactiveMessage.message}
                </p>
              </div>
            </div>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowProactiveBubble(false);
              }}
              className="absolute top-2 right-2 p-1 hover:bg-zinc-800 rounded-full"
            >
              <X className="w-3 h-3 text-zinc-500" />
            </button>
          </div>
          {/* Pointer arrow */}
          <div className="absolute -bottom-2 right-4 w-4 h-4 bg-zinc-900 border-r border-b border-amber-500/30 transform rotate-45" />
        </div>
      )}

      {/* Launcher Button - 40x40px, semi-transparent */}
      {!isOpen && (
        <button
          onClick={() => {
            setShowProactiveBubble(false);
            setIsOpen(true);
          }}
          style={{
            position: 'fixed',
            ...launcherPosition,
            zIndex: 9998,
            opacity: isScrolling ? 0.3 : (showProactiveBubble ? 1 : 0.85),
            transition: 'opacity 0.3s ease, transform 0.2s ease',
          }}
          className={`w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg hover:opacity-100 hover:scale-110 group ${showProactiveBubble ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-zinc-900' : ''}`}
          data-testid="chatbot-launcher"
          aria-label="Open chat support"
        >
          <MessageCircle className="w-5 h-5 text-black" />
          
          {/* Pulse animation for proactive message */}
          {showProactiveBubble && (
            <span className="absolute inset-0 rounded-full bg-amber-500 animate-ping opacity-40" />
          )}
          
          {/* Pulse animation for new users */}
          {!showProactiveBubble && messages.length === 0 && (
            <span className="absolute inset-0 rounded-full bg-amber-500 animate-ping opacity-30" />
          )}
        </button>
      )}}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{
            bottom: isMobile ? '16px' : '24px',
            right: isMobile ? '16px' : '24px',
            left: isMobile ? '16px' : 'auto',
            width: isMobile ? 'auto' : '380px',
            height: isMobile ? 'calc(100vh - 100px)' : '520px',
            maxHeight: isMobile ? 'calc(100vh - 100px)' : '520px',
          }}
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-black" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">SlabBot</h3>
                <p className="text-xs text-zinc-400">Marketplace Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                  title="Clear chat"
                >
                  <Trash2 className="w-4 h-4 text-zinc-500 hover:text-red-400" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Welcome message */}
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-amber-400" />
                </div>
                <h4 className="text-white font-bold mb-2">Hi! I'm SlabBot</h4>
                <p className="text-zinc-400 text-sm mb-4">
                  I can help with marketplace rules, shipping, fees, and more!
                </p>
                
                {/* Quick suggestions */}
                <div className="space-y-2">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="block w-full text-left px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-zinc-300 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat messages */}
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-amber-500 text-black rounded-br-md'
                      : msg.isError
                        ? 'bg-red-500/20 text-red-300 border border-red-500/30 rounded-bl-md'
                        : 'bg-zinc-800 text-zinc-100 rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                    <span className="text-sm text-zinc-400">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t border-zinc-800 bg-zinc-900">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask about rules, shipping, fees..."
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-zinc-500 focus:ring-2 focus:ring-amber-500/50 focus:border-transparent outline-none"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className="p-2.5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 disabled:cursor-not-allowed rounded-xl transition-colors"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4 text-black" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default AIChatbot;
